// general-settings.component.ts
import { Component, Input, NgZone, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataEnvService } from 'src/app/shared/sl-data/data-env.service';
import { SettingsData } from 'src/app/shared/sl-data/settings';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Question } from 'src/app/shared/sl-data/settings/settings-types';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CreateQuestionModalComponent } from '../create-worker-role-modal/create-question-modal.component';
import { ChangeDetectorRef } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss']
})
export class GeneralSettingsComponent implements OnInit {
  generalSettingsForm: FormGroup;
  @Input() actionInProgress$: BehaviorSubject<boolean>;
  constructor(
    private fb: FormBuilder,
    private env: DataEnvService,
    private settings: SettingsData,
    private utilities: UtilitiesService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.generalSettingsForm = this.fb.group({
      minDriverPictures: [3, [Validators.required, Validators.min(1)]],
      summaryNote: ['', Validators.required],
      deliveryProof: ['', Validators.required],
      hideAndSkipDOTForm: [false, Validators.required],
      dotForm: [[], []],
      minCharsFailedJobNote: [10, [Validators.required, Validators.min(1)]],
      minCharsRejectComment: [10, [Validators.required, Validators.min(1)]],
      minCharsSummaryNote: [10, [Validators.required, Validators.min(1)]],
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      const generalSettings = await this.settings.getGeneralSettings();
        this.generalSettingsForm.patchValue({
            minDriverPictures: generalSettings.minDriverPictures || 3,
            summaryNote: generalSettings.summaryNote || '',
            deliveryProof: generalSettings.deliveryProof || '',
            dotForm: Array.isArray(generalSettings.dotForm) ? generalSettings.dotForm : [],
            hideAndSkipDOTForm: generalSettings.hideAndSkipDOTForm || false,
            minCharsFailedJobNote: generalSettings.minCharsFailedJobNote || 10,
            minCharsRejectComment: generalSettings.minCharsRejectComment || 10,
            minCharsSummaryNote: generalSettings.minCharsSummaryNote || 10,
        });
    } catch (error) {
        this.utilities.notifyError('Unexpected error loading settings. Please contact system admin');
    }
  }

  async onSubmit(): Promise<void> {
    if (this.generalSettingsForm.valid) {
      try {
        this.actionInProgress$.next(true);
        await this.settings.updateGeneralSettings({
            minDriverPictures: this.generalSettingsForm.get('minDriverPictures')?.value,
            summaryNote: this.generalSettingsForm.get('summaryNote')?.value,
            deliveryProof: this.generalSettingsForm.get('deliveryProof')?.value,
            dotForm: this.generalSettingsForm.get('dotForm')?.value,
            hideAndSkipDOTForm: this.generalSettingsForm.get('hideAndSkipDOTForm')?.value,
            minCharsFailedJobNote: this.generalSettingsForm.get('minCharsFailedJobNote')?.value,
            minCharsRejectComment: this.generalSettingsForm.get('minCharsRejectComment')?.value,
            minCharsSummaryNote: this.generalSettingsForm.get('minCharsSummaryNote')?.value,

          }
        );

        this.utilities.notifySuccess('Settings saved successfully.');
      } catch (err) {
        if(err.details){
            this.utilities.notifyError(err.details);
            return;
        }
        this.utilities.notifyError('Unexpected error saving settings. Please contact system admin');
      }
      finally{
        this.actionInProgress$.next(false);
      }
    } else {
      this.generalSettingsForm.markAllAsTouched();
    }
  }

  public removeItem(question: Question){
    const questions: Question[] = this.generalSettingsForm.get('dotForm').value;
    const newQuestions = questions.filter(q=>q.order!==question.order);
    newQuestions.forEach(q=>{
      if(q.order>question.order){
        q.order = q.order - 1;
      }
    });
    this.generalSettingsForm.patchValue({
      dotForm: newQuestions
    });
  }

  editItem(question: Question) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = { question };
  
    const dialogRef = this.dialog.open(CreateQuestionModalComponent, dialogConfig);
  
    dialogRef.afterClosed().subscribe((result: Question) => {
      if (!result) return;
      // ALWAYS re-read the array inside the callback
      const questions: Question[] = [
        ...this.generalSettingsForm.get('dotForm')!.value
      ];
  
      // Update items
      const index = questions.findIndex(q => q.order === result.order);
      if (index !== -1) {
        questions[index] = result;
      } else {
        result.order = questions.length + 1;
        questions.push(result);
      }
  
      // Now patch the NEW array (new reference!)
      this.generalSettingsForm.patchValue({
        dotForm: questions
      });
      this.cdr.markForCheck();
    });
  }

  dropped(event: CdkDragDrop<Question, Question>) {
    const currentQuestions: Question[] = [...this.generalSettingsForm.get('dotForm')!.value];
    const [removed] = currentQuestions.splice(event.previousIndex, 1);
    currentQuestions.splice(event.currentIndex, 0, removed);
    // Update the order property for consistency
    currentQuestions.forEach((q, i) => {
      q.order = i + 1;
    });

    this.generalSettingsForm.patchValue({
      dotForm: currentQuestions
    });
    
  }
}